import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarClock, CheckCircle, XCircle, Clock, User, Video } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePendingMeetingsToComplete, PendingMeetingToComplete } from '@/hooks/usePendingMeetingsToComplete';

const STORAGE_KEY = 'dismissed_pending_meetings';
const DISMISS_DURATION_MS = 10 * 60 * 1000; // 10 minutes

// LocalStorage helpers
const getDismissedMeetings = (): Map<string, number> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Map();
    return new Map(JSON.parse(stored));
  } catch {
    return new Map();
  }
};

const saveDismissedMeeting = (id: string, timestamp: number) => {
  try {
    const dismissed = getDismissedMeetings();
    dismissed.set(id, timestamp);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...dismissed]));
  } catch (e) {
    console.error('Error saving dismissed meeting:', e);
  }
};

const clearDismissedMeeting = (id: string) => {
  try {
    const dismissed = getDismissedMeetings();
    dismissed.delete(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...dismissed]));
  } catch (e) {
    console.error('Error clearing dismissed meeting:', e);
  }
};

export function PendingMeetingsReminderModal() {
  const { t } = useTranslation();
  const { pendingMeetings, completeMeeting, cancelMeeting } = usePendingMeetingsToComplete();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Find the first non-dismissed meeting
  const currentMeeting = pendingMeetings.find(m => !dismissedIds.has(m.id));

  // Handle dismiss - hide for 10 minutes
  const handleDismiss = (meetingId: string) => {
    saveDismissedMeeting(meetingId, Date.now());
    setDismissedIds(prev => new Set([...prev, meetingId]));
  };

  // Handle complete meeting
  const handleComplete = async () => {
    if (!currentMeeting) return;
    
    setLoading(true);
    const success = await completeMeeting(currentMeeting.id);
    setLoading(false);

    if (success) {
      clearDismissedMeeting(currentMeeting.id);
      toast.success('Reunião marcada como completada!', {
        description: 'O consultor receberá um lembrete para confirmar a conexão.',
      });
    } else {
      toast.error('Erro ao completar reunião');
    }
  };

  // Handle cancel meeting
  const handleCancel = async () => {
    if (!currentMeeting) return;
    
    setLoading(true);
    const success = await cancelMeeting(currentMeeting.id, 'Reunião não realizada');
    setLoading(false);
    setShowCancelConfirm(false);

    if (success) {
      clearDismissedMeeting(currentMeeting.id);
      toast.success('Reunião cancelada');
    } else {
      toast.error('Erro ao cancelar reunião');
    }
  };

  // Effect to check for expired dismissals (reappear after 10 minutes)
  useEffect(() => {
    const checkDismissals = () => {
      const now = Date.now();
      const dismissed = getDismissedMeetings();
      const stillDismissed = new Set<string>();

      dismissed.forEach((timestamp, id) => {
        if (now - timestamp < DISMISS_DURATION_MS) {
          stillDismissed.add(id);
        } else {
          // Expired, remove from storage
          clearDismissedMeeting(id);
        }
      });

      setDismissedIds(stillDismissed);
    };

    // Check immediately
    checkDismissals();

    // Check every minute
    const interval = setInterval(checkDismissals, 60 * 1000);
    return () => clearInterval(interval);
  }, [pendingMeetings]);

  // Cleanup dismissed IDs when meetings are no longer pending
  useEffect(() => {
    const pendingIds = new Set(pendingMeetings.map(m => m.id));
    
    setDismissedIds(prev => {
      const updated = new Set<string>();
      prev.forEach(id => {
        if (pendingIds.has(id)) {
          updated.add(id);
        } else {
          clearDismissedMeeting(id);
        }
      });
      return updated;
    });
  }, [pendingMeetings]);

  if (!currentMeeting) return null;

  const scheduledDate = new Date(currentMeeting.scheduledFor);
  const relativeTime = formatDistanceToNow(scheduledDate, { addSuffix: true, locale: ptBR });

  return (
    <>
      <AlertDialog open={!!currentMeeting && !showCancelConfirm} onOpenChange={() => {}}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-warning/20">
                <CalendarClock className="h-6 w-6 text-warning" />
              </div>
              <AlertDialogTitle className="text-lg">
                Reunião Pendente de Completar
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Você tem uma reunião agendada que já passou. Por favor, indique se ela foi realizada.
                </p>

                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  {currentMeeting.title && (
                    <div className="flex items-center gap-2">
                      <Video className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{currentMeeting.title}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {format(scheduledDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </span>
                    <Badge variant="outline" className="text-warning border-warning/50 bg-warning/10">
                      {relativeTime}
                    </Badge>
                  </div>

                  {(currentMeeting.contactName || currentMeeting.leadName) && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{currentMeeting.contactName || currentMeeting.leadName}</span>
                    </div>
                  )}

                  {currentMeeting.hostName && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Consultor: {currentMeeting.hostName}</span>
                    </div>
                  )}

                  {currentMeeting.hoursOverdue >= 2 && (
                    <Badge variant="destructive" className="text-xs">
                      {currentMeeting.hoursOverdue}h de atraso
                    </Badge>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  A reunião foi realizada?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => handleDismiss(currentMeeting.id)}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Lembrar depois
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowCancelConfirm(true)}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Não ocorreu
            </Button>
            <Button
              onClick={handleComplete}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Sim, completar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cancelamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar esta reunião? Isso indicará que a reunião não foi realizada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar reunião
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
